import { useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useListPaintings, useListCategories, useListCollections } from "@/lib/api";
import { PaintingCard } from "@/components/painting-card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { SlidersHorizontal, Search } from "lucide-react";
import { useLocale } from "@/context/locale-context";

export default function Gallery() {
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState(searchParams.get("categoryId") || "");
  const [collectionId, setCollectionId] = useState(searchParams.get("collectionId") || "");
  const [year, setYear] = useState(searchParams.get("year") || "");          // 👈 añadido
  const [featuredOnly, setFeaturedOnly] = useState(searchParams.get("featured") === "true");
  const [onSaleOnly, setOnSaleOnly] = useState(searchParams.get("onSale") === "true");

  const { data: categories } = useListCategories();
  const { data: collections } = useListCollections();

  const currentYear = new Date().getFullYear();                               // 👈 añadido
  const years = Array.from({ length: 20 }, (_, i) => currentYear - i);      // 👈 añadido

  const params = {};
  if (categoryId) params.categoryId = parseInt(categoryId);
  if (collectionId) params.collectionId = parseInt(collectionId);
  if (year) params.year = parseInt(year);                                     // 👈 añadido
  if (featuredOnly) params.featured = true;
  if (onSaleOnly) params.onSale = true;
  if (search.trim()) params.search = search.trim();

  const { data: paintings, isLoading } = useListPaintings(params);

  return (
    <div className="min-h-screen bg-background" data-testid="gallery-page">
      <div className="container mx-auto px-4 md:px-8 py-16">
        <div className="mb-12">
          <h1 className="text-5xl font-serif mb-4">{t.gallery.title}</h1>
          <p className="text-muted-foreground text-lg max-w-2xl">{t.gallery.subtitle}</p>
        </div>
        <div className="flex flex-col md:flex-row gap-12">
          <aside className="md:w-64 shrink-0">
            <div className="space-y-8 sticky top-24">
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest mb-3">
                  <Search className="w-3.5 h-3.5" />{t.gallery.search}
                </div>
                <Input type="search" placeholder={t.gallery.searchPlaceholder} value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none" data-testid="gallery-search" />
              </div>
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest mb-3">
                  <SlidersHorizontal className="w-3.5 h-3.5" />{t.gallery.filters}
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{t.gallery.category}</p>
                    <Select value={categoryId || "all"} onValueChange={(v) => setCategoryId(v === "all" ? "" : v)}>
                      <SelectTrigger className="rounded-none w-full" data-testid="category-filter">
                        <SelectValue placeholder={t.gallery.allCategories} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t.gallery.allCategories}</SelectItem>
                        {categories?.map(cat => (
                          <SelectItem key={cat.id} value={String(cat.id)}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{t.gallery.collection}</p>
                    <Select value={collectionId || "all"} onValueChange={(v) => setCollectionId(v === "all" ? "" : v)}>
                      <SelectTrigger className="rounded-none w-full" data-testid="collection-filter">
                        <SelectValue placeholder={t.gallery.allCollections} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t.gallery.allCollections}</SelectItem>
                        {collections?.map((collection) => (
                          <SelectItem key={collection.id} value={String(collection.id)}>{collection.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Filtro de año */}
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{t.gallery.year}</p>
                    <Select value={year || "all"} onValueChange={(v) => setYear(v === "all" ? "" : v)}>
                      <SelectTrigger className="rounded-none w-full" data-testid="year-filter">
                        <SelectValue placeholder={t.gallery.allYears} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t.gallery.allYears}</SelectItem>
                        {years.map(y => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between py-2">
                    <Label htmlFor="featured-toggle" className="text-sm cursor-pointer">{t.gallery.featuredOnly}</Label>
                    <Switch id="featured-toggle" checked={featuredOnly} onCheckedChange={setFeaturedOnly} data-testid="featured-toggle" />
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <Label htmlFor="sale-toggle" className="text-sm cursor-pointer">{t.gallery.onSaleOnly}</Label>
                    <Switch id="sale-toggle" checked={onSaleOnly} onCheckedChange={setOnSaleOnly} data-testid="sale-toggle" />
                  </div>
                </div>
              </div>
            </div>
          </aside>
          <div className="flex-1">
            {!isLoading && (
              <p className="text-sm text-muted-foreground mb-8 uppercase tracking-widest" data-testid="results-count">
                {paintings?.length ?? 0} {t.gallery.worksFound}
              </p>
            )}
            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {Array(6).fill(0).map((_, i) => <Skeleton key={i} className="aspect-[4/5] w-full rounded-none" />)}
              </div>
            ) : paintings?.length === 0 ? (
              <div className="py-24 text-center text-muted-foreground">{t.gallery.noResults}</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {paintings?.map(painting => <PaintingCard key={painting.id} painting={painting} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}